import { useState, useRef, useEffect } from "react";
import {
    Mail,
    BracesIcon,
    InfoIcon,
    SettingsIcon,
} from "lucide-react";
import { Button } from "@component/button";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { Dialog, DialogContent } from "@component/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@component/select";
import { Tooltip } from "@component/tooltip";
import { getHrJobListings, getListCompanies } from "@service/job";
import { InviteToApplyRequest, Job } from "@type/job";
import { EmailTemplate } from "@type/setting";
import { useUserProfileContext } from "@context/user_profile";
import { useNavigate } from "react-router-dom";
import EmailVariablesPanel from "@container/email-variable-panel";
import TextEditor, { TextEditorRef } from "./rich-editor/v2/text-editor";
import { Company } from "@type/company";
import { defaultPagination } from "@constant/app";
import { getEmailTemplates } from "@service/employer";
import { defaultAssessmentInvitationTemplate, defaultHireTemplate, defaultInterviewInvitationTemplate, defaultJobInvitationTemplate, defaultRejectTemplate } from "@constant/email";
import { useCompanySubdomain } from "@context/subdomain_context";
import { debounce } from "lodash";
import { mergeEmailTemplates } from "@util/email";

interface Props {
    isOpen: boolean;
    onClose: (open: boolean) => void;
    candidateName: string;
    onConfirm: (data: InviteToApplyRequest) => void;
    candidate_id?: string; // New prop to pass candidate ID
    currentJob?: Job | null;
}

export default function InviteConfirmationDialogV2({
    isOpen,
    onClose,
    candidateName,
    onConfirm,
    candidate_id = "",
    currentJob = null
}: Props) {
    const navigate = useNavigate();
    const [, companyInfo] = useCompanySubdomain()
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const [jobs, setJobs] = useState<Job[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<string>("");
    const [selectedJob, setSelectedJob] = useState<Job | undefined>(undefined);
    const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
    const [listTemplates, setListTemplates] = useState<EmailTemplate[]>([]);
    const [showVariablesPanel, setShowVariablesPanel] = useState(false);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [lastFocusedField, setLastFocusedField] = useState<"subject" | "content">("content");
    // Refs for form elements
    const subjectInputRef = useRef<HTMLInputElement>(null);
    const contentEditorRef = useRef<TextEditorRef>(null);

    const reloadTemplates = () => {
        getEmailTemplates().then((resp) => {
            if (resp.status === 200) {
                const hardcodedDefaults: EmailTemplate[] = [
                    {
                        template_id: "default_reject",
                        template_name: "Standard Rejection Email",
                        template_category: "reject",
                        email_subject: defaultRejectTemplate(companyInfo).subject,
                        email_content: defaultRejectTemplate(companyInfo).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_hire",
                        template_name: "Standard Job Offer Email",
                        template_category: "hire",
                        email_subject: defaultHireTemplate(companyInfo).subject,
                        email_content: defaultHireTemplate(companyInfo).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_interview_invitation",
                        template_name: "Standard Interview Invitation Email",
                        template_category: "interview-invitation",
                        email_subject: defaultInterviewInvitationTemplate(companyInfo).subject,
                        email_content: defaultInterviewInvitationTemplate(companyInfo).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_job_invitation",
                        template_name: "Standard Job Invitation Email",
                        template_category: "job-invitation",
                        email_subject: defaultJobInvitationTemplate(companyInfo).subject,
                        email_content: defaultJobInvitationTemplate(companyInfo).content,
                        is_default: true,
                    },
                    {
                        template_id: "default_assessment_invitation",
                        template_name: "Standard Screening Invitation Email",
                        template_category: "assessment-invitation",
                        email_subject: defaultAssessmentInvitationTemplate(7).subject,
                        email_content: defaultAssessmentInvitationTemplate(7).content,
                        is_default: true,
                    },
                ];
                const merged = mergeEmailTemplates(resp.data.data, hardcodedDefaults);
                setListTemplates(merged);
                setSelectedTemplate(merged.find(t => t.template_id === "default_job_invitation") ?? null);
            }
        })
    }

    useEffect(() => {
        if (isOpen) {
            reloadTemplates()
            setSelectedJob(undefined);
        }
        return () => {
            setSelectedTemplate(null);
        }
    }, [isOpen])

    const handleTemplateChange = (templateID: string) => {
        const template = listTemplates.find(t => t.template_id === templateID);
        if (template) {
            setSelectedTemplate(template);
        } else {
            setSelectedTemplate(null);
        }
    };

    const fetchJobPosting = async (companyID: string) => {
        const limit = !isLoadingUserProfile && ["admin", "super_admin"].includes(userInfo?.role || "") ? 50 : 20;
        const resp = await getHrJobListings({
            keyword: "",
            page: defaultPagination.page,
            limit: limit,
            status: "Published",
            visibility: "Public,Private",
            candidate_id: candidate_id,
            company_id: companyID,
            without_collaborators: true,
        });

        if (resp.data.code === 200) {
            setJobs(resp.data.data)
        }
    }

    const fetchAllCompanies = async () => {
        getListCompanies().then((response) => {
            if (response.status === 200) {
                setCompanies(response.data.data);
            }
        });
    }

    useEffect(() => {
        if (!isLoadingUserProfile && userInfo && isOpen) {
            if (!["admin", "super_admin"].includes(userInfo?.role || "")) {
                fetchJobPosting("")
            }
        }
    }, [userInfo, isLoadingUserProfile, isOpen]);

    useEffect(() => {
        if (["admin", "super_admin"].includes(userInfo?.role || "") && isOpen) {
            fetchAllCompanies();
        }
    }, [isOpen, userInfo]);

    const handleConfirm = () => {
        onConfirm({
            job_id: currentJob ? currentJob.id : selectedJob?.id || "",
            user_id: candidate_id,
            email_subject: selectedTemplate?.email_subject || "",
            email_body: selectedTemplate?.email_content || "",
        });
        onClose(false);
    };

    const onChangeJob = (jobId: string) => {
        const job = jobs.find((j) => j.id === jobId) || undefined
        setSelectedJob(job)
    }

    // Reset selected position when company changes
    const handleCompanyChange = (companyId: string) => {
        setSelectedCompany(companyId);
        fetchJobPosting(companyId);
    };

    const resetForm = () => {
        setSelectedCompany("");
        setSelectedTemplate(null);

        setShowVariablesPanel(false);
    };

    const handleCancel = () => {
        resetForm();
        onClose(false);
    };

    const handleInsertVariable = (variable: string) => {
        // Get references to both fields using refs
        const subjectInput = subjectInputRef.current;
        const contentEditor = contentEditorRef.current;

        // Insert into the last focused field
        if (lastFocusedField === "subject" && subjectInput) {
            const start = subjectInput.selectionStart || 0;
            const end = subjectInput.selectionEnd || 0;
            const newSubject =
                selectedTemplate?.email_subject.substring(0, start) +
                variable +
                selectedTemplate?.email_subject.substring(end);
            setSelectedTemplate((prev) => prev ? { ...prev, email_subject: newSubject } : null);

            // Set cursor position after the inserted variable
            setTimeout(() => {
                subjectInput.focus();
                subjectInput.setSelectionRange(
                    start + variable.length,
                    start + variable.length
                );
            }, 0);
        } else if (contentEditor) {
            // Insert text at current cursor position in the rich text editor
            contentEditor.insertText(variable);

            // Focus the editor after insertion
            setTimeout(() => {
                contentEditor.focus();
            }, 0);
        } else {
            // Fallback: append to end of content
            setSelectedTemplate((prev) => prev ? { ...prev, email_content: (prev.email_content + " " + variable) } : null);
        }
    };


    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[1400px] w-[98vw] h-[90vh] p-0 gap-0 flex flex-col overflow-hidden">
                {/* Single Unified Container */}
                <div className="flex flex-col flex-1 min-h-0 p-6">
                    {/* Header - Fixed */}
                    <div className="mb-6 flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                                <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            <h2 className="text-lg font-semibold text-foreground">
                                Invite to Apply
                            </h2>
                        </div>
                    </div>

                    {/* Main Content - Two Column Layout */}
                    <div className="flex flex-1 min-h-0 gap-6 overflow-hidden">
                        {/* Left Column - Candidate Name, Company, and Job Selection */}
                        <div
                            className="space-y-5 overflow-y-auto pr-2"
                            style={{
                                paddingLeft: "8px",
                                width: showVariablesPanel ? "33.33%" : "50%",
                                transition: "width 0.3s ease",
                            }}
                        >
                            {/* Candidate Name */}
                            <div>
                                <Label
                                    htmlFor="candidate-name"
                                    className="text-sm font-medium text-foreground"
                                >
                                    Candidate Name
                                </Label>
                                <Input
                                    id="candidate-name"
                                    value={candidateName}
                                    disabled
                                    className="mt-2 text-foreground bg-gray-50 dark:bg-gray-800"
                                />
                            </div>
                            {currentJob ? (
                                <>
                                    <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 space-y-3">
                                        <div>
                                            <Label className="text-xs font-medium text-muted-foreground mb-1">
                                                Job Title
                                            </Label>
                                            <Input
                                                value={currentJob.job_title}
                                                disabled
                                                className="text-sm font-medium bg-white dark:bg-gray-900"
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <Label className="text-xs font-medium text-muted-foreground mb-1">
                                                    Department
                                                </Label>
                                                <Input
                                                    value={currentJob.department}
                                                    disabled
                                                    className="text-sm bg-white dark:bg-gray-900"
                                                />
                                            </div>
                                            <div>
                                                <Label className="text-xs font-medium text-muted-foreground mb-1">
                                                    Location
                                                </Label>
                                                <Input
                                                    value={currentJob.location}
                                                    disabled
                                                    className="text-sm bg-white dark:bg-gray-900"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Company Selection */}
                                    {["admin", "super_admin"].includes(userInfo?.role || "") && (
                                        <div>
                                            <Label
                                                htmlFor="company-select"
                                                className="text-sm font-medium text-foreground"
                                            >
                                                Select Company
                                            </Label>
                                            <Select
                                                value={selectedCompany}
                                                onValueChange={handleCompanyChange}
                                            >
                                                <SelectTrigger className="w-full mt-2">
                                                    <SelectValue placeholder="Select a company" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {companies.map((company) => (
                                                        <SelectItem key={company.id} value={company.id}>
                                                            {company.company_name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}

                                    {/* Job Position Selection */}
                                    <div>
                                        <Label
                                            htmlFor="job-position"
                                            className="text-sm font-medium text-foreground"
                                        >
                                            Select Job Listing
                                        </Label>
                                        <Select
                                            value={selectedJob?.id || ""}
                                            onValueChange={onChangeJob}
                                            disabled={["admin", "super_admin"].includes(userInfo?.role || "") && !selectedCompany ? true : false}
                                        >
                                            <SelectTrigger className="w-full mt-2">
                                                <SelectValue
                                                    placeholder={
                                                        "Select a job position"
                                                    }
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {jobs.length === 0 ? (
                                                    <SelectItem value="none" disabled>
                                                        No job postings available
                                                    </SelectItem>
                                                ) : (
                                                    <>
                                                        {jobs.map((job, index) => (
                                                            <SelectItem key={index} value={job.id}>
                                                                {job.job_title}
                                                                {job?.is_invited && (
                                                                    <span className="px-2 py-1 rounded-md ml-2 text-xs text-blue-600 border border-blue-100 bg-blue-100">
                                                                        Already Applied
                                                                    </span>
                                                                )}
                                                            </SelectItem>
                                                        ))}
                                                    </>
                                                )}
                                            </SelectContent>

                                        </Select>

                                        {/* Job Details Banner */}
                                        {selectedJob && (
                                            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 space-y-3">
                                                <div>
                                                    <Label className="text-xs font-medium text-muted-foreground mb-1">
                                                        Job Title
                                                    </Label>
                                                    <Input
                                                        value={selectedJob.job_title}
                                                        disabled
                                                        className="text-sm font-medium bg-white dark:bg-gray-900"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <Label className="text-xs font-medium text-muted-foreground mb-1">
                                                            Department
                                                        </Label>
                                                        <Input
                                                            value={selectedJob.department}
                                                            disabled
                                                            className="text-sm bg-white dark:bg-gray-900"
                                                        />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs font-medium text-muted-foreground mb-1">
                                                            Location
                                                        </Label>
                                                        <Input
                                                            value={selectedJob.location}
                                                            disabled
                                                            className="text-sm bg-white dark:bg-gray-900"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}


                            {/* Email Template Selection */}
                            <div>
                                <div className="flex items-center gap-2">
                                    <Label className="text-sm font-medium text-foreground">
                                        Email Template
                                    </Label>
                                    <Tooltip side="right" content={(
                                        <p>
                                            You can edit this email template but the original
                                            template won't be affected.
                                        </p>
                                    )}>
                                        <InfoIcon className="w-4 h-4 text-blue-600 cursor-help" />
                                    </Tooltip>
                                </div>
                                <div className="flex items-center gap-2 mt-2">
                                    <Select
                                        value={selectedTemplate?.template_id || ""}
                                        onValueChange={handleTemplateChange}
                                    >
                                        <SelectTrigger className="flex-1">
                                            <SelectValue placeholder="Choose a template" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {listTemplates.filter((template) => template.template_category === 'job-invitation').length === 0 ? (
                                                <SelectItem value="none" disabled>
                                                    No templates available
                                                </SelectItem>
                                            ) : (
                                                <>
                                                    {listTemplates.filter((template) => template.template_category === 'job-invitation').map((template, key) => (
                                                        <SelectItem key={key} value={template.template_id || ""}>
                                                            <div className="flex items-center justify-between w-full">
                                                                <span>{template.template_name}</span>
                                                                {template.is_default && (
                                                                    <span
                                                                        className="ml-2 px-2 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-full"
                                                                        style={{
                                                                            paddingTop: "4px",
                                                                            paddingBottom: "4px",
                                                                        }}
                                                                    >
                                                                        Default
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </SelectItem>
                                                    ))}
                                                </>
                                            )}
                                        </SelectContent>
                                    </Select>

                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="px-3"
                                        onClick={() => {
                                            navigate("/setting?tab=email-communication&template=job-invitation");
                                        }}
                                    >
                                        <SettingsIcon className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Email Subject and Content */}
                        <div
                            className="flex flex-col min-h-0 overflow-y-auto pl-2"
                            style={{
                                paddingRight: "8px",
                                width: showVariablesPanel ? "33.33%" : "50%",
                                transition: "width 0.3s ease",
                            }}
                        >
                            {/* Subject */}
                            <div className="mb-4">
                                <div className="flex items-center gap-2">
                                    <Label
                                        htmlFor="email-subject"
                                        className="text-sm font-medium text-foreground"
                                    >
                                        Email Subject
                                    </Label>
                                    <Tooltip side="right" content={(
                                        <p>Personalize your email subject using variables.</p>
                                    )}>
                                        <InfoIcon className="w-4 h-4 text-blue-600 cursor-help" />
                                    </Tooltip>
                                </div>
                                <Input
                                    ref={subjectInputRef}
                                    value={selectedTemplate?.email_subject || ""}
                                    onChange={(e) => setSelectedTemplate((prev) => prev ? { ...prev, email_subject: e.target.value } : null)}
                                    onFocus={() => setLastFocusedField("subject")}
                                    disabled={!selectedTemplate}
                                    className="mt-2 text-foreground"
                                />
                            </div>

                            <div className="flex items-center justify-between mb-2">
                                <Label
                                    htmlFor="email-content"
                                    className="text-sm font-medium text-foreground mb-2 flex-shrink-0"
                                >
                                    Email Content
                                </Label>
                                <button
                                    type="button"
                                    onClick={() => setShowVariablesPanel(!showVariablesPanel)}
                                    className="text-xs text-blue-600 hover:text-black flex items-center gap-1"
                                >
                                    <BracesIcon className="h-3 w-3" />
                                    {showVariablesPanel ? "Hide" : "Show"} Variables
                                </button>
                            </div>
                            <TextEditor
                                ref={contentEditorRef}
                                defaultValue={selectedTemplate?.email_content || ""}
                                disabled={!selectedTemplate}
                                onChange={(content) => {
                                    setSelectedTemplate((prev) => prev ? { ...prev, email_content: content } : null);
                                }}
                                onFocus={() => setLastFocusedField("content")}
                            />
                            <p
                                className={`text-xs mt-3 flex-shrink-0 text-muted-foreground`}
                            >
                                {showVariablesPanel
                                    ? "Click on Variables in the right panel to insert them to your email subject or content."
                                    : "Click the Variables button to see all available personalization options for your email subject or content."}
                            </p>
                        </div>

                        {/* Email Variables Panel */}
                        {showVariablesPanel && (
                            <div className="flex-shrink-0 pl-4" style={{ width: "33.33%" }}>
                                <EmailVariablesPanel
                                    onClose={() => setShowVariablesPanel(false)}
                                    onInsertVariable={handleInsertVariable}
                                    isEditMode={true}
                                />
                            </div>
                        )}
                    </div>

                    {/* Footer - Fixed */}
                    <div className="mt-6 pt-4 border-t border-border flex-shrink-0">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-muted-foreground">
                                This will send an email to <b>{candidateName}</b> with information about
                                the position and a link to apply.
                            </p>
                            <div className="flex gap-3">
                                <Button variant="outline" onClick={handleCancel}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={debounce(handleConfirm, 300)}
                                    disabled={(!currentJob && !selectedJob) || !selectedTemplate}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
                                    style={{ paddingRight: "16px", paddingLeft: "16px" }}
                                >
                                    Send Invitation
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog >
    );
}
